import {useMemo} from 'react';
import {useAuth} from '../context/AuthContext';
import {canViewTickets, canCreateTickets, canAssignTickets, canManageTickets, isTicketsAdmin} from '../utils/ticketPerms';

export interface TicketPerms {
	canView: boolean;
	canCreate: boolean;
	/** TICKETS_ASSIGN — bulk assign. */
	canAssign: boolean;
	/** TICKETS_MANAGE — any updateTicket-backed write (status, assignee, resolve). */
	canManage: boolean;
	isAdmin: boolean;
}

/**
 * Component-facing permission seam (ACC-440). Reads the granular Forecast
 * ticket permissions off the auth context and returns memoized action flags
 * that mirror the gateway's enforcement (ACC-468). Fails closed when there is
 * no authenticated user.
 */
export function useTicketPerms(): TicketPerms {
	const {user} = useAuth();
	const ticketPerms = user?.ticketPerms;
	return useMemo(() => {
		const perms = ticketPerms ?? [];
		return {
			canView: canViewTickets(perms),
			canCreate: canCreateTickets(perms),
			canAssign: canAssignTickets(perms),
			canManage: canManageTickets(perms),
			isAdmin: isTicketsAdmin(perms),
		};
		// Recompute when the user's ticketPerms identity changes.
	}, [ticketPerms]);
}
